import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from '../../store'
import { Avatar, Chip, Icon } from '../ui'
import { initialsOf } from '../rail/format'
import { staffAuth } from '../../lib/staffAuth'
import { CoverageToggle } from './CoverageToggle'

const ROLE_LABEL: Record<'dispatch' | 'tech', string> = {
  dispatch: 'Dispatch',
  tech: 'Tech',
}

/** Honest fallback name when identity has not resolved yet — never a guess at a real one. See `store.me`'s own doc comment / src/lib/identity.ts. */
const UNKNOWN_NAME = 'You'
/** Honest fallback avatar initials for the same case — deliberately not letter-shaped, so it never reads as a guessed real person's initials. */
const UNKNOWN_INITIALS = '—'

/** Verbatim from docs/design-handoff/tossker-redesign/README.md §6 "Account menu". */
const ROLE_LOCKED_COPY = 'Set by an admin in Settings › Users — not something you switch yourself.'

/**
 * The account menu — docs/design-handoff/tossker-redesign/README.md §6
 * "Account menu", 320px, radius 14. Three blocks:
 *
 *   1. Identity — avatar, name, email.
 *   2. State — a read-only role pill, and the one real self-service control
 *      here, the coverage switch.
 *   3. Items — Settings, History and search, Sign out.
 *
 * Task 27: reads the real signed-in identity off `store.me` (see
 * src/lib/identity.ts). `me` is `null` until a real `GET /ops/whoami`
 * resolves one — the honest fallback below is what renders until then.
 *
 * Task 31: "Sign out" is wired for real. It calls the staff auth client's
 * `signOut()` (src/lib/staffAuth.ts — POST /auth/signout, which is the only
 * thing that can actually clear the HttpOnly session/refresh cookies), then
 * clears local identity state regardless of whether that network call
 * succeeded — an honest "signed out" locally beats silently keeping a stale
 * identity around after a failed request. No explicit navigation call here:
 * flipping `authStatus` to `'signed-out'` is enough — whichever
 * `RequireAuth` (src/components/auth/AuthGate.tsx) is currently mounted
 * around the active route reacts to that and redirects to `/sign-in` on its
 * own.
 *
 * NO APPEARANCE CONTROL (task-2 brief, binding decision 1). README §6
 * proposes a Light/Dark/System segmented pill in the state block. That is
 * obsolete: Phase 1 already shipped a six-choice theme picker
 * (`ThemeChoice` — Tossker/Legacy × Day/Night/Auto) in Settings → Personal
 * preferences (`p.theme`), which a three-way pill in this menu could not
 * even fully express. Shipping both would be exactly the bug the header's
 * ☀/🌙 button was deleted to fix: two controls answering the same question,
 * free to disagree. There is deliberately no dedicated "Appearance" row
 * either — the single "Settings" item below already lands on the personal
 * pane where the real picker lives, so a second entry pointing at the same
 * screen would just be the "ONE SETTINGS ENTRY" problem again (see that
 * item's own comment).
 *
 * ROLE IS READ-ONLY HERE, ALWAYS (users-and-permissions design §1/§6). The
 * pill below is decoration, not a control: no `onClick`, no menu, nothing
 * that writes `store.role`. `AdminCheckbox` and `RoleSegment` — a header
 * checkbox that flipped the console's own store flag with no server round
 * trip, and a role segment that let a person self-service `dispatch`, which
 * is what grants reach-across-lanes in gates.ts — were both deleted for
 * exactly this reason, and `POST /ops/coverage` now refuses a `role` key
 * outright (`role-not-settable`). Role is admin-assigned only, through the
 * real Users pane (`PATCH /ops/users/:id`) inside Settings.
 *
 * COVERAGE IS THE ONE REAL SELF-SERVICE CONTROL (binding decision 6).
 * `<CoverageToggle/>` is unchanged internally — it still calls
 * `ops.setCoverage`, waits for the server's own answer before touching the
 * store, leaves the store untouched on refusal, and toasts either way. Only
 * its mounting location moved, from the header to here (see its own comment
 * for what else moved with it, and what didn't).
 *
 * WHO ELSE IS COVERING (fix round 1, task-2 review). Rendered here as a
 * named avatar list, straight off `peers.filter(p => p.covering)` —
 * deliberately NOT the same list `SignedInPeers` builds (which filters on
 * `p.online`). `online` and `covering` are independent facts (see the `Peer`
 * type's own doc comment in `store.ts`): a tech can be covering with the
 * laptop shut, which makes them invisible in the header roster while the
 * aggregate pill (and this account avatar's own green dot) correctly still
 * says someone is covering. Without this list there was no way to find out
 * *who* in exactly that case.
 */
export function InitialsMenu() {
  const me = useStore((s) => s.me)
  const role = useStore((s) => s.role)
  const covering = useStore((s) => s.covering)
  const peers = useStore((s) => s.peers)
  const admin = useStore((s) => s.admin)
  const set = useStore((s) => s.set)
  const setMe = useStore((s) => s.setMe)
  const setAuthStatus = useStore((s) => s.setAuthStatus)
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  const displayName = me?.name ?? UNKNOWN_NAME
  const avatarInitials = me ? initialsOf(me.name) : UNKNOWN_INITIALS
  // Same aggregate CoverageToggle reads: mine OR any covering peer, so a
  // colleague covering while I step away never reads as "coverage off" —
  // including on this, the account avatar's own green dot.
  const anyoneCovering = covering || peers.some((p) => p.covering)
  // Fix round 1 (task-2 review): deliberately NOT filtered by `p.online`.
  // `online` and `covering` are independent (store.ts's own Peer doc comment
  // — "someone can be online without covering, or covering with the laptop
  // shut"), and `SignedInPeers` in the header only ever shows peers where
  // `online` is true. Reusing that same list here would make a covering
  // peer who has since closed their laptop invisible everywhere: the
  // aggregate pill above would still say "Covering" (it doesn't check
  // `online` either), with no way to find out who. Built straight off
  // `peers.filter(covering)`, same as the pre-fix-round-1 CoverageToggle did.
  const coveringPeers = peers.filter((p) => p.covering && p.id !== me?.id)

  const close = () => setOpen(false)

  // ONE ENTRY, because there was only ever one destination. "Personal
  // preferences" and "System settings" both opened /settings and differed only
  // in which pane was selected — two menu items for one screen, and a person
  // choosing between them had no way to know that. Lands on the personal pane,
  // which is the one somebody opening their own menu almost always wants —
  // and, as of this task, is also where the real Appearance picker lives.
  //
  // The read-only hint went with it: the settings page already says
  // "Not admin · tenant settings read-only" at the top, where it is true of the
  // thing you are looking at rather than a prediction about it.
  const goSettings = () => {
    set({ page: 'settings', settingsTab: 'personal' })
    navigate('/settings')
    close()
  }
  const goHistory = () => {
    set({ page: 'history' })
    navigate('/history')
    close()
  }
  // Users USED TO be a third entry here (`goUsers`, admin-only, navigating
  // to its own `/users` route) until it moved into Settings as a pane
  // (`components/settings/UsersSection.tsx`, 2026-08-10). Same reasoning
  // that removed the earlier "Personal preferences"/"System settings" split
  // just above applies again: it opened the exact same screen as `goSettings`
  // and differed only in which pane was selected, which is one destination
  // wearing two menu items. Reaching it is now identical to reaching any
  // other settings section — `goSettings` then the nav — so there is nothing
  // left for a dedicated entry to do.
  const signOut = () => {
    close()
    void staffAuth
      .signOut()
      .finally(() => {
        setMe(null)
        setAuthStatus('signed-out')
        set({ page: null, role: 'dispatch', admin: false, superAdmin: false })
      })
      .catch(() => {
        // staffAuth.signOut() is documented to never reject on its own — this
        // guards only against a defensive future change to that contract
        // surfacing as an unhandled rejection here. Local state above has
        // already cleared by the time this would ever run.
      })
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="inline-flex items-center gap-1 rounded-full border border-subtle py-0.5 pl-0.5 pr-1.5"
      >
        <span className="relative inline-flex">
          <Avatar initials={avatarInitials} role={me ? role : undefined} size={28} />
          {anyoneCovering ? (
            <span
              aria-hidden="true"
              data-testid="account-covering-dot"
              className="absolute -bottom-0.5 -right-0.5 h-[9px] w-[9px] rounded-full border border-card bg-ok"
            />
          ) : null}
        </span>
        <Icon name="chevron-down" size={14} className="text-lo" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-2 w-[320px] overflow-hidden rounded-[14px] border border-subtle bg-elevated shadow-menu"
        >
          {/* 1. Identity block */}
          <div className="flex items-center gap-3 border-b border-subtle px-4 py-3">
            <Avatar initials={avatarInitials} role={me ? role : undefined} size={42} />
            <div className="min-w-0">
              <p className="truncate font-display text-[15px] font-semibold text-hi">{displayName}</p>
              {me?.email ? <p className="truncate text-[12px] text-mid">{me.email}</p> : null}
            </div>
          </div>

          {/* 2. State block. Fix round 2 (phase-2 whole-phase review,
              finding 3): --surface-sunken and --surface-raised (this menu's
              own --surface-raised background, via `bg-elevated` above)
              resolved to the identical literal in both Day themes, so this
              block flattened into the menu around it — invisible even
              though `.bg-sunken` was correctly present (test/shell.test.tsx
              only asserted the class, not that it painted anything
              different). Fixed at the token layer (tokens.css) so
              --surface-sunken genuinely differs from --surface-raised in
              every theme now; the border here is belt-and-suspenders, the
              same treatment ThreadHeader.tsx's peer pill already uses for
              the identical "needs separation from a raised surface" case. */}
          <div className="flex flex-col gap-2 border-b border-subtle bg-sunken px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[13px] text-hi">Role</span>
              {/* Fix round 2 (finding 7): `tone="warn"` used a STATUS colour
                  for an identity fact — and disagreed with the Avatar just
                  above, which colours by --role-dispatch/--role-tech, so a
                  Legacy dispatcher saw a pink avatar beside an orange pill
                  for the same fact. `tone={role}` reuses the exact tokens
                  Avatar reads (tokens.css: "ROLE COLOUR MEANS ROLE"). */}
              <Chip tone={role}>
                <Icon name="lock" size={12} />
                {ROLE_LABEL[role]}
              </Chip>
            </div>
            <p className="text-[11px] text-lo">{ROLE_LOCKED_COPY}</p>
            <div className="flex items-center justify-between gap-2 pt-1">
              <span className="text-[13px] text-hi">Covering the queue</span>
              <CoverageToggle />
            </div>
            {coveringPeers.length > 0 ? (
              <div className="flex flex-col gap-1.5 pt-1" aria-label="Other staff covering">
                <span className="text-[11px] uppercase tracking-wide text-lo">Also covering</span>
                {coveringPeers.map((p) => (
                  <div key={p.id} data-testid={`covering-peer-${p.id}`} className="flex items-center gap-2">
                    <Avatar initials={initialsOf(p.name)} role={p.role} size={20} title={p.name} />
                    <span className="text-[12px] text-hi">{p.name}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {admin ? (
              <p className="flex items-center gap-1.5 pt-1 text-[11px] text-warn">
                <Icon name="shield-check" size={13} />
                Admin — you can change workspace settings
              </p>
            ) : null}
          </div>

          {/* 3. Items */}
          <div className="py-1">
            <button
              role="menuitem"
              type="button"
              data-testid="identity-menu-settings"
              onClick={goSettings}
              className="flex w-full items-center gap-2.5 bg-tint-primary px-4 py-2.5 text-left text-[13px] text-hi hover:brightness-95"
            >
              <Icon name="settings" size={17} />
              Settings
            </button>
            <button
              role="menuitem"
              type="button"
              onClick={goHistory}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] text-hi hover:bg-card"
            >
              <Icon name="archive" size={17} />
              History and search
            </button>
            <button
              role="menuitem"
              type="button"
              onClick={signOut}
              className="flex w-full items-center gap-2.5 border-t border-subtle px-4 py-2.5 text-left text-[13px] text-bad hover:bg-card"
            >
              <Icon name="log-out" size={17} />
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
